import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Grid, Typography, Button, Alert,
  TextField, MenuItem, Divider, Chip, Table, TableHead, TableRow,
  TableCell, TableBody, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, InputAdornment, CircularProgress
} from '@mui/material';
import {
  ArrowBack, CheckCircle, Add, OpenInNew, Download, Delete, FolderZip, RuleFolder, Search, CloudUpload
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import PageHeader from '../common/PageHeader';
import StatusChip from '../common/StatusChip';
import { useAuth } from '../../contexts/AuthContext';

export default function BOEDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canDelete = isAdmin;

  const [boe, setBoe] = useState(null);
  const [remittances, setRemittances] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [knockoffDialog, setKnockoffDialog] = useState(false);
  const [remSearch, setRemSearch] = useState('');
  const [remStatusFilter, setRemStatusFilter] = useState('all');
  const [manualKnockoffDialog, setManualKnockoffDialog] = useState(false);
  const [deleteKnockoffDialog, setDeleteKnockoffDialog] = useState(null);
  const [form, setForm] = useState({
    remittance_id: '', invoice_id: '', knockoff_date: new Date().toISOString().split('T')[0],
    amount: '', bank_reference: '', notes: ''
  });
  const [manualForm, setManualForm] = useState({
    ref_no: '', knockoff_date: new Date().toISOString().split('T')[0], notes: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = () => api.get(`/boe/${id}`).then(r => setBoe(r.data.data));
  useEffect(() => {
    load();
    api.get('/remittances/filter/pending-boe').then(r => setRemittances(r.data.data));
    api.get('/invoices').then(r => setInvoices(r.data.data.filter(i => ['submitted', 'paid'].includes(i.status))));
  }, [id]);

  const openKnockoffDialog = () => {
    setForm({
      remittance_id: '', invoice_id: '',
      knockoff_date: new Date().toISOString().split('T')[0],
      amount: boe?.invoice_value || '',
      bank_reference: '', notes: ''
    });
    setError('');
    setKnockoffDialog(true);
  };

  const openManualKnockoffDialog = () => {
    setManualForm({ ref_no: '', knockoff_date: new Date().toISOString().split('T')[0], notes: '' });
    setError('');
    setManualKnockoffDialog(true);
  };

  const handleKnockoff = async () => {
    if (!form.remittance_id || !form.amount || !form.knockoff_date) {
      setError('Remittance, amount and date required');
      return;
    }
    setLoading(true);
    try {
      await api.post(`/boe/${id}/knockoff`, form);
      setKnockoffDialog(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Error recording knockoff');
    } finally { setLoading(false); }
  };

  const handleManualKnockoff = async () => {
    setLoading(true);
    try {
      await api.post(`/boe/${id}/manual-knockoff`, manualForm);
      setManualKnockoffDialog(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Error marking knockoff');
    } finally { setLoading(false); }
  };

  const handleDeleteKnockoff = async () => {
    try {
      await api.delete(`/boe/knockoff/${deleteKnockoffDialog.id}`);
      setDeleteKnockoffDialog(null);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Error deleting knockoff');
      setDeleteKnockoffDialog(null);
    }
  };

  const viewDoc = async (filePath) => {
    if (!filePath) return;
    const filename = filePath.split('/').pop();
    try {
      const res = await api.get(`/boe/files/${filename}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (err) {
      setError('Unable to open file. Please try again.');
    }
  };

  const sanitize = (s) => s.replace(/[^a-zA-Z0-9\-]/g, '');

  const downloadDoc = async (filePath, label) => {
    if (!filePath) return;
    const filename = filePath.split('/').pop();
    const ext = filePath.split('.').pop() || 'pdf';
    try {
      const res = await api.get(`/boe/files/${filename}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sanitize(label)}.${ext}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      setError('Unable to download file.');
    }
  };

  const [uploadingDoc, setUploadingDoc] = useState(null);
  const uploadDoc = async (type, file) => {
    if (!file) return;
    setUploadingDoc(type);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', type);
      await api.post(`/boe/${id}/document`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed. Allowed: PDF, Excel, images (max 10 MB).');
    } finally {
      setUploadingDoc(null);
    }
  };

  const downloadAll = async () => {
    try {
      const res = await api.get(`/boe/${id}/download-all`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sanitize(boe.boe_number)}_docs.zip`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      setError('Unable to download documents.');
    }
  };

  if (!boe) return null;

  const dutyTotal = parseFloat(boe.duty_amount || 0);
  const hasAnyDoc = boe.boe_file_path || boe.invoice_file_path || boe.awb_file_path || boe.fedex_invoice_path;

  return (
    <Box>
      <PageHeader
        title={`BOE: ${boe.boe_number}`}
        subtitle={`${boe.boe_date} · ${boe.beneficiary_name || 'Unknown Supplier'}`}
        breadcrumbs={[{ label: 'BOE', path: '/boe' }, { label: boe.boe_number }]}
        action={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button startIcon={<ArrowBack />} onClick={() => navigate('/boe')}>Back</Button>
            {boe.status !== 'knocked_off' && (
              <>
                <Button variant="outlined" startIcon={<RuleFolder />} onClick={openManualKnockoffDialog}
                  sx={{ borderColor: 'warning.main', color: 'warning.dark', '&:hover': { borderColor: 'warning.dark', bgcolor: 'warning.50' } }}>
                  Manual Knock-off
                </Button>
                <Button variant="contained" startIcon={<Add />} onClick={openKnockoffDialog}>
                  Record Knock-off
                </Button>
              </>
            )}
          </Box>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={2.5}>
        {/* BOE Details */}
        <Grid item xs={12} md={5}>
          <Card sx={{ mb: 2.5 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1">BOE Details</Typography>
                <StatusChip status={boe.status} />
              </Box>
              <Grid container spacing={1.5}>
                {[
                  { label: 'BOE / CBE-XIII Number', value: boe.boe_number, mono: true },
                  ...(boe.hawb_number ? [{ label: 'HAWB Number', value: boe.hawb_number, mono: true }] : []),
                  { label: 'Date', value: boe.boe_date },
                  { label: 'Port of Entry', value: boe.port_of_entry },
                  { label: 'Supplier', value: boe.beneficiary_name || '—' },
                  { label: 'Currency', value: boe.currency },
                  { label: 'Invoice Value (USD)', value: boe.invoice_value ? `USD ${parseFloat(boe.invoice_value).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—' },
                  { label: 'Assessable Value (INR)', value: boe.assessable_value ? `₹${parseFloat(boe.assessable_value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '—' },
                ].map(item => (
                  <Grid item xs={6} key={item.label}>
                    <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                    <Typography variant="body2" fontWeight={500} sx={item.mono ? { fontFamily: 'monospace', fontSize: 11 } : {}}>
                      {item.value}
                    </Typography>
                  </Grid>
                ))}
              </Grid>

              {/* Duty Breakdown */}
              {(boe.bcd_amount || boe.aidc_amount || boe.sw_surcharge || boe.igst_amount) && (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>Duty Breakdown (INR)</Typography>
                  <Grid container spacing={1}>
                    {boe.bcd_amount && (
                      <Grid item xs={6}><Typography variant="caption" color="text.secondary">BCD</Typography>
                        <Typography variant="body2">₹{parseFloat(boe.bcd_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</Typography></Grid>
                    )}
                    {boe.aidc_amount && (
                      <Grid item xs={6}><Typography variant="caption" color="text.secondary">AIDC</Typography>
                        <Typography variant="body2">₹{parseFloat(boe.aidc_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</Typography></Grid>
                    )}
                    {boe.sw_surcharge && (
                      <Grid item xs={6}><Typography variant="caption" color="text.secondary">SW Surcharge</Typography>
                        <Typography variant="body2">₹{parseFloat(boe.sw_surcharge).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</Typography></Grid>
                    )}
                    {boe.igst_amount && (
                      <Grid item xs={6}><Typography variant="caption" color="text.secondary">IGST (ITC)</Typography>
                        <Typography variant="body2" color="text.secondary">₹{parseFloat(boe.igst_amount).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</Typography></Grid>
                    )}
                    <Grid item xs={12}>
                      <Box sx={{ p: 1, bgcolor: '#e8eaf6', borderRadius: 1, mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Duty Total (BCD + AIDC + SW)</Typography>
                        <Typography variant="subtitle2" fontWeight={700} color="primary.main">
                          ₹{dutyTotal.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </>
              )}

              {boe.notes && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">Notes</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>{boe.notes}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Documents */}
          <Card sx={{ mb: 2.5 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle1">Documents</Typography>
                {hasAnyDoc && (
                  <Tooltip title="Download all as ZIP">
                    <Button size="small" variant="outlined" startIcon={<FolderZip />} onClick={downloadAll}
                      sx={{ fontSize: 11 }}>
                      Download All
                    </Button>
                  </Tooltip>
                )}
              </Box>
              <Grid container spacing={1}>
                {[
                  { label: 'BOE Document',         path: boe.boe_file_path,       type: 'boe',           color: 'primary',   dlName: `BOEEntry${boe.boe_number}` },
                  { label: 'Commercial Invoice',   path: boe.invoice_file_path,   type: 'invoice',       color: 'info',      dlName: `CommercialInvoice${boe.boe_number}` },
                  { label: 'AWB (FedEx)',           path: boe.awb_file_path,       type: 'awb',           color: 'secondary', dlName: `AWB${boe.boe_number}` },
                  { label: 'FedEx Tax Invoice',    path: boe.fedex_invoice_path,  type: 'fedex_invoice', color: 'warning',   dlName: `FedExTaxInvoice${boe.boe_number}` },
                ].map(doc => (
                  <Grid item xs={12} key={doc.label}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, border: '1px solid', borderColor: doc.path ? 'primary.light' : 'divider', borderRadius: 2 }}>
                      <Typography variant="body2">{doc.label}</Typography>
                      {doc.path ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip label="Uploaded" size="small" color={doc.color} icon={<CheckCircle />} />
                          <Tooltip title="View">
                            <IconButton size="small" onClick={() => viewDoc(doc.path)} sx={{ color: 'primary.main' }}>
                              <OpenInNew fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download">
                            <IconButton size="small" onClick={() => downloadDoc(doc.path, doc.dlName)} sx={{ color: 'text.secondary' }}>
                              <Download fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Chip label="Not uploaded" size="small" variant="outlined" color="default" />
                          <Button component="label" size="small" variant="outlined"
                            disabled={uploadingDoc === doc.type}
                            startIcon={uploadingDoc === doc.type ? <CircularProgress size={14} /> : <CloudUpload fontSize="small" />}
                            sx={{ fontSize: 11 }}>
                            {uploadingDoc === doc.type ? 'Uploading…' : 'Upload'}
                            <input hidden type="file" accept=".pdf,.png,.jpg,.jpeg,.xls,.xlsx"
                              onChange={e => { uploadDoc(doc.type, e.target.files[0]); e.target.value = ''; }} />
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Line Items */}
          {boe.items && boe.items.length > 0 && (
            <Card>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ px: 2.5, py: 2 }}>
                  <Typography variant="subtitle1">Line Items</Typography>
                </Box>
                <Divider />
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>SKU</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>HS Code</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell>Unit</TableCell>
                        <TableCell align="right">Unit Value</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {boe.items.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{item.sku || '—'}</TableCell>
                          <TableCell>{item.description}</TableCell>
                          <TableCell sx={{ fontSize: 11 }}>{item.hs_code || '—'}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell>{item.unit}</TableCell>
                          <TableCell align="right">{item.unit_value}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{item.total_value}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Knockoff History */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ px: 2.5, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1">Knock-off Records</Typography>
                <Chip
                  label={boe.status === 'knocked_off' ? 'Fully Knocked Off' : boe.status === 'partial' ? 'Partially Knocked Off' : 'Pending Knock-off'}
                  color={boe.status === 'knocked_off' ? 'success' : boe.status === 'partial' ? 'warning' : 'error'}
                  size="small"
                />
              </Box>
              <Divider />
              {!boe.knockoffs?.length ? (
                <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant="body2">No knock-off recorded yet</Typography>
                  <Typography variant="caption">Click "Record Knock-off" to link this BOE to a remittance</Typography>
                </Box>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Remittance Ref</TableCell>
                      <TableCell>Invoice</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Bank Ref</TableCell>
                      <TableCell>Status</TableCell>
                      {canDelete && <TableCell align="center">Del</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(boe.knockoffs || []).map((k) => (
                      <TableRow key={k.id}>
                        <TableCell sx={{ fontSize: 12 }}>{k.knockoff_date}</TableCell>
                        <TableCell sx={{ fontSize: 12, fontFamily: 'monospace' }}>{k.remittance_number}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{k.invoice_number || '—'}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12, fontWeight: 600 }}>
                          {parseFloat(k.amount).toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ fontSize: 11 }}>{k.bank_reference || '—'}</TableCell>
                        <TableCell><StatusChip status={k.status} /></TableCell>
                        {canDelete && (
                          <TableCell align="center">
                            <IconButton size="small" onClick={() => setDeleteKnockoffDialog(k)} sx={{ color: 'error.main' }}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {(boe.boe_file_path || boe.invoice_file_path || boe.awb_file_path) && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Documents already uploaded on this BOE will be automatically used for the knock-off record.
            </Alert>
          )}
        </Grid>
      </Grid>

      {/* Knockoff Dialog */}
      <Dialog open={knockoffDialog} onClose={(_, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') setKnockoffDialog(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>Record BOE Knock-off</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, mt: 1 }}>
            Link this BOE to the outward remittance for Kotak portal knock-off.
            {(boe.boe_file_path || boe.invoice_file_path || boe.awb_file_path) && (
              <span> Existing BOE documents will be automatically applied.</span>
            )}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={8}>
              <TextField size="small" fullWidth placeholder="Search remittance / beneficiary / kotak ref"
                value={remSearch} onChange={e => setRemSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
            </Grid>
            <Grid item xs={4}>
              <TextField size="small" select fullWidth label="Status" value={remStatusFilter}
                onChange={e => setRemStatusFilter(e.target.value)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Outward Remittance *" select fullWidth value={form.remittance_id}
                onChange={(e) => setForm({ ...form, remittance_id: e.target.value })}>
                <MenuItem value="">-- Select Remittance --</MenuItem>
                {remittances
                  .filter(r => {
                    const q = remSearch.toLowerCase();
                    const matchSearch = !q ||
                      r.remittance_number?.toLowerCase().includes(q) ||
                      r.beneficiary_name?.toLowerCase().includes(q) ||
                      r.kotak_reference?.toLowerCase().includes(q);
                    const matchStatus = remStatusFilter === 'all' || (r.remittance_status || 'open') === remStatusFilter;
                    return matchSearch && matchStatus;
                  })
                  .map(r => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.kotak_reference || r.remittance_number} · {r.beneficiary_name} · {r.currency} {parseFloat(r.amount).toLocaleString()}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Invoice (Optional)" select fullWidth value={form.invoice_id}
                onChange={(e) => setForm({ ...form, invoice_id: e.target.value })}>
                <MenuItem value="">-- Select Invoice --</MenuItem>
                {invoices.map(i => (
                  <MenuItem key={i.id} value={i.id}>
                    {i.invoice_number} · {i.currency} {parseFloat(i.total_amount).toLocaleString()}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField label="Knock-off Date *" type="date" fullWidth value={form.knockoff_date}
                onChange={(e) => setForm({ ...form, knockoff_date: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Amount *" type="number" fullWidth value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder={boe.invoice_value ? String(boe.invoice_value) : ''} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Bank / Kotak Reference" fullWidth value={form.bank_reference}
                onChange={(e) => setForm({ ...form, bank_reference: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Notes" fullWidth multiline rows={2} value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setKnockoffDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleKnockoff} disabled={loading} startIcon={<CheckCircle />}>
            {loading ? 'Saving...' : 'Record Knock-off'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Knock-off Dialog */}
      <Dialog open={manualKnockoffDialog} onClose={(_, r) => { if (r !== 'backdropClick' && r !== 'escapeKeyDown') setManualKnockoffDialog(false); }} maxWidth="xs" fullWidth>
        <DialogTitle>Manual Knock-off</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, mt: 1 }}>
            Mark this BOE as knocked off without linking to a remittance. The reference number is optional.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField label="Knock-off Date" type="date" fullWidth value={manualForm.knockoff_date}
                onChange={(e) => setManualForm({ ...manualForm, knockoff_date: e.target.value })}
                InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Reference No. (Optional)" fullWidth value={manualForm.ref_no}
                onChange={(e) => setManualForm({ ...manualForm, ref_no: e.target.value })}
                placeholder="e.g. Kotak / Bank ref" />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Notes (Optional)" fullWidth multiline rows={2} value={manualForm.notes}
                onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setManualKnockoffDialog(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleManualKnockoff} disabled={loading} startIcon={<RuleFolder />}>
            {loading ? 'Saving...' : 'Mark as Knocked Off'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Knockoff Confirmation */}
      <Dialog open={Boolean(deleteKnockoffDialog)} onClose={() => setDeleteKnockoffDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Knock-off Record</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this knock-off record? This will revert the BOE status.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteKnockoffDialog(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteKnockoff}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
